"use client";
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Switch, Paper, Stack, 
  CircularProgress, Alert, Grid, IconButton, Collapse, Button
} from "@mui/material";
import {
  AssignmentTurnedInRounded,
  FilterListRounded,
  LaunchRounded,
  KeyboardArrowDownRounded
} from "@mui/icons-material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getAssignedFormsByClient } from "../../../../../api/services/Assessment/AssignAssessment";
import { QK } from "../../../../../base/qk";
import useClientDetails from "../../../../../hooks/useClientDetails";

const AssessmentsTabs = () => {
  const [showData, setShowData] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const { id: clientID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { fullName } = useClientDetails(clientID);

  const { data: assignedForms, isLoading, isError } = useQuery(
    [QK.assignedForms, clientID],
    () => getAssignedFormsByClient(clientID),
    { enabled: !!clientID, staleTime: 30000 }
  );

  const handleGoToAssessment = (formID) => {
    navigate(`/active-assessments/${clientID}#${formID}`);
  };

  useEffect(() => {
    const hash = location.hash;
    if (hash && assignedForms?.length > 0) {
      setShowData(true);
      const elementId = hash.replace("#", "");
      
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
          element.style.borderLeft = "10px solid #6366f1";
          element.style.backgroundColor = alpha("#6366f1", 0.1);
          
          setTimeout(() => {
            element.style.borderLeft = "1px solid #f1f5f9";
            element.style.backgroundColor = "#fff";
          }, 3000);
        }
      }, 500);
    }
  }, [location.hash, assignedForms]);

  return (
    <Box sx={{ width: "100%", py: 1 }}>
      
      <Button
        fullWidth
        onClick={() => setShowData(!showData)}
        variant="contained"
        endIcon={<KeyboardArrowDownRounded sx={{ 
            transform: showData ? "rotate(180deg)" : "rotate(0deg)", 
            transition: "0.3s" 
        }} />}
        sx={{
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
          borderRadius: 2,
          fontWeight: 600,
          textTransform: "none",
          mb: 2,
          bgcolor: "#ffffff",
          color: "#0f172a",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
          "&:hover": { 
            bgcolor: "#f8fafc",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AssignmentTurnedInRounded sx={{ fontSize: 20, color: '#4f46e5' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
            Clinical Assessments for {fullName}
          </Typography>
        </Stack>
      </Button>

      <Collapse in={showData}>
        <Paper
          elevation={0}
          sx={{ 
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f8fafc",
            mb: 2.5,
            border: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center" 
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterListRounded sx={{ color: "#64748b", fontSize: 18 }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: "#64748b" }}>
              {assignedForms?.length || 0} Modules Assigned
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: "#64748b" }}>Inactive</Typography>
            <Switch 
              size="small" 
              checked={showInactive} 
              onChange={(e) => setShowInactive(e.target.checked)} 
              color="primary" 
            />
          </Stack>
        </Paper>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} thickness={5} sx={{ color: "#6366f1" }} />
          </Box>
        ) : isError ? (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            Failed to sync assessment modules.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {assignedForms?.map((af) => (
              <Grid item xs={12} sm={6} md={4} key={af.id}>
                <Paper
                  elevation={0}
                  id={af.form}
                  onClick={() => handleGoToAssessment(af.form)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#6366f1",
                      bgcolor: alpha("#6366f1", 0.02),
                      transform: "translateX(4px)",
                      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.06)",
                      "& .launch-icon": { color: "#6366f1", opacity: 1 },
                    }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ overflow: 'hidden', flex: 1 }}>
                    <Box sx={{ 
                      width: 4, 
                      height: 24, 
                      borderRadius: 1, 
                      bgcolor: "#e2e8f0", 
                      flexShrink: 0 
                    }} />
                    <Box sx={{ overflow: 'hidden', flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        noWrap 
                        sx={{ 
                          fontWeight: 600, 
                          color: "#1e293b", 
                          fontSize: '0.8rem',
                          letterSpacing: '0.01em'
                        }}
                      >
                        {af.form_title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 400, display: 'block', fontSize: '0.65rem' }}>
                        {new Date(af.assigned_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton 
                    className="launch-icon" 
                    sx={{ 
                      opacity: 0.2, 
                      transition: "0.2s",
                      padding: 0.5
                    }}
                  >
                    <LaunchRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Collapse>
    </Box>
  );
};

const alpha = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default AssessmentsTabs;